import { useTranslation } from "react-i18next";
import type { ReactNode } from "react";
import { FacilityIcon, ManualIcon } from "@/components/icons";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";

export interface CategorySectionProps {
  title: string;
  /** UNFILTERED total in this section (the badge) — stays stable while a search hides rows. */
  total: number;
  /** True when there are no rows to show (either genuinely empty, or the search filtered them all out). */
  isEmpty: boolean;
  emptyMessage: string;
  children: ReactNode;
}

/**
 * A labelled category group ("On this facility" / "Available in the library") with a count badge and either the row
 * list or a per-section empty message. Kept dumb — the parent owns filtering + which rows to pass.
 */
export function CategorySection({ title, total, isEmpty, emptyMessage, children }: CategorySectionProps) {
  const { t } = useTranslation();
  return (
    <section aria-label={title} className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground flex items-center gap-2 text-xs font-semibold tracking-wide uppercase">
          {title}
          <span className="bg-muted text-muted-foreground inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[0.7rem] leading-none">
            {total}
          </span>
        </h3>
        {/* Column headers — name what the per-row numbers mean. Aligned above the row columns (same w-9 + pr-3). The
            manual column is a placeholder until the manual table lands. Styled react-aria tooltips (snappy delay), not
            the native `title` (slow, browser-controlled); each header is a role=img with an accessible name. */}
        {!isEmpty ? (
          <div className="text-muted-foreground flex items-center pr-3">
            <TooltipTrigger delay={200}>
              <span role="img" aria-label={t("facilities.Raw")} className="flex w-9 cursor-default justify-center">
                <FacilityIcon aria-hidden="true" className="size-4" />
              </span>
              <Tooltip>{t("facilities.Raw")}</Tooltip>
            </TooltipTrigger>
            <TooltipTrigger delay={200}>
              <span role="img" aria-label={t("manuals.raw")} className="flex w-9 cursor-default justify-center">
                <ManualIcon aria-hidden="true" className="size-4" />
              </span>
              <Tooltip>{t("manuals.raw")}</Tooltip>
            </TooltipTrigger>
          </div>
        ) : null}
      </div>
      {isEmpty ? (
        <p className="text-muted-foreground border-border rounded-md border border-dashed px-3 py-6 text-center text-sm">
          {emptyMessage}
        </p>
      ) : (
        <ul className="divide-border/70 border-border flex flex-col divide-y overflow-hidden rounded-md border">{children}</ul>
      )}
    </section>
  );
}
